import React, { useEffect, useCallback, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { useDropzone } from 'react-dropzone'

import { UploadCloud, RefreshCw, ChevronLeft, ChevronRight, MoreHorizontal, X, Film, Download, Trash2, Play } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'

import {
  UPLOADING,
  FAILED,
  QUEUED,
  COMPLETE,
  CANCELLED,
  DUPLICATE,
  NO_VERSION,
  RECENT,
  LONG_TERM,
  PAGE_SIZE_OPTIONS,
  VERSION_FILTER_ALL,
  LINK_MODE_PLAY,
  filterLongTermFiles,
  longTermVersionOptions,
  requestLongTermVersionFilter,
  requestOpenFileDelete,
  requestCloseFileDelete,
  requestConfirmFileDelete,
  requestHubUploads,
  requestSelectVersion,
  requestHandleNewFiles,
  requestRemoveRejected,
  requestClearFinishedUploads,
  requestFileLink,
  requestCloseFileLink,
  requestPage,
  requestPageSize
} from '../../stores/uploads'

import LongTermUploadProgress from '../../components/LongTermUploadProgress'
import ProxyPlayer from '../../components/ProxyPlayer'

function UploadDropzone({ disabled, disabledText, onDrop }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled,
    noClick: false,
  })

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex h-full min-h-[3.25rem] items-center gap-3 rounded-lg border-2 border-dashed px-4 py-2 transition-colors',
        disabled
          ? 'cursor-not-allowed border-border bg-secondary/40 opacity-60'
          : 'cursor-pointer border-primary/50 bg-card hover:border-primary hover:bg-accent/40',
        isDragActive && !disabled && 'border-primary bg-accent/60'
      )}
    >
      <input {...getInputProps()} />
      <UploadCloud className={cn('h-6 w-6 shrink-0', disabled ? 'text-muted-foreground' : 'text-primary')} />
      <div className="min-w-0">
        <p className={cn('text-sm font-medium leading-tight', disabled ? 'text-muted-foreground' : 'text-foreground')}>
          {isDragActive ? 'Drop files here…' : 'Drag & drop files here, or click to select'}
        </p>
        <p className="text-xs text-muted-foreground">{disabled ? disabledText : 'Multiple files supported'}</p>
      </div>
    </div>
  )
}

// ProxyPlayer handles its shortcuts (Space, arrows, J/A/F...) on its own focusable wrapper, so it must own the focus
function FocusedProxyPlayer(props) {
  const ref = useRef(null)

  useEffect(() => {
    const wrapper = ref.current && ref.current.querySelector('.wrapper[tabindex]')
    if (wrapper) wrapper.focus({preventScroll: true})
  }, [props.proxyLink])

  return (
    <div ref={ref}>
      <ProxyPlayer {...props} />
    </div>
  )
}

// Radix Select items cannot have an empty value; the store uses '' for "all Versions"
const ALL_VERSIONS_ITEM = '__all__'

const rejectionText = r => {
  if (r.reason === DUPLICATE) return 'This file is already in the upload list.'
  if (r.reason === NO_VERSION) return 'Select a Version before adding files.'
  return 'This file could not be added.'
}

const fmtDateTime = val => {
  if (!val) return ''
  const d = new Date(val)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`
}

// Paged file list shared by Recent Uploads and Long Term Storage
function FilesTable({ files, page, pageSize, onPage, onPageSize, loading, emptyText, dateLabel, requestFileLink, onDelete }) {
  const numberOfPages = Math.max(Math.ceil(files.length / pageSize), 1)
  const pageFiles = useMemo(
    () => files.slice((page - 1) * pageSize, page * pageSize),
    [files, page, pageSize]
  )

  if (loading && files.length === 0) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
        <Spinner size={18} /> Loading files…
      </div>
    )
  }

  if (files.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground">{emptyText}</div>
  }

  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, files.length)

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead className="min-w-[11rem] whitespace-nowrap">Version</TableHead>
              <TableHead className="min-w-[9rem] whitespace-nowrap">Uploaded By</TableHead>
              <TableHead className="whitespace-nowrap">Size</TableHead>
              <TableHead className="whitespace-nowrap">{dateLabel}</TableHead>
              <TableHead className="whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageFiles.map(file => (
              <TableRow key={file.id}>
                <TableCell className="break-words font-medium">
                  {file.proxyAvailable ? (
                    <button
                      type="button"
                      onClick={() => requestFileLink(file, 'proxy', LINK_MODE_PLAY)}
                      title="View proxy in player"
                      className="break-words text-left font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {file.name}
                    </button>
                  ) : file.name}
                </TableCell>
                <TableCell className="text-muted-foreground" title={file.programName || ''}>{file.versionName || ''}</TableCell>
                <TableCell className="text-muted-foreground">{file.uploadedByContactName || ''}</TableCell>
                <TableCell className="whitespace-nowrap">{file.fileSize}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtDateTime(file.createdDate)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon-sm" aria-label={`Actions for ${file.name}`} title="Actions">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem disabled={!file.proxyAvailable} onSelect={() => requestFileLink(file, 'proxy', LINK_MODE_PLAY)}>
                        <Play className="mr-2 h-4 w-4" /> {file.proxyAvailable ? 'View proxy in player' : 'Proxy not available yet'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem disabled={!file.sourceAvailable} onSelect={() => requestFileLink(file, 'source')}>
                        <Download className="mr-2 h-4 w-4" /> Download source
                      </DropdownMenuItem>
                      <DropdownMenuItem disabled={!file.proxyAvailable} onSelect={() => requestFileLink(file, 'proxy')}>
                        <Film className="mr-2 h-4 w-4" /> Download proxy
                      </DropdownMenuItem>
                      {onDelete && file.canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => onDelete(file)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete from long term storage
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>{`Showing ${first}–${last} of ${files.length}`}</span>
          <span aria-hidden="true">·</span>
          <label className="flex items-center gap-1">
            Rows per page
            <Select value={String(pageSize)} onValueChange={v => onPageSize(parseInt(v, 10))}>
              <SelectTrigger className="h-7 w-[4.5rem] px-2 text-xs" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="xs" onClick={() => onPage(page - 1)} disabled={page <= 1}>
            <ChevronLeft className="h-3.5 w-3.5" /> Previous
          </Button>
          <span className="px-2">Page {page} of {numberOfPages}</span>
          <Button variant="outline" size="xs" onClick={() => onPage(page + 1)} disabled={page >= numberOfPages}>
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </>
  )
}

function Uploads({
  fetched,
  loading,
  hasError,
  error,
  eligible,
  uploadsAccessChecked,
  showUploadsTab,
  account,
  versions,
  selectedVersionId,
  recentUploads,
  recentPage,
  recentPageSize,
  longTermEnabled,
  longTermFiles,
  longTermPage,
  longTermPageSize,
  longTermVersionFilter,
  uploads,
  rejected,
  fileLink,
  fileDelete,
  requestHubUploads,
  requestPageSize,
  requestLongTermVersionFilter,
  requestOpenFileDelete,
  requestCloseFileDelete,
  requestConfirmFileDelete,
  requestSelectVersion,
  requestHandleNewFiles,
  requestRemoveRejected,
  requestClearFinishedUploads,
  requestFileLink,
  requestCloseFileLink,
  requestPage,
}) {
  // Only load the tab data when the account has access (decided at login)
  useEffect(() => {
    if (showUploadsTab && !fetched) requestHubUploads()
  }, [showUploadsTab]) // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before leaving while uploads are pending
  useEffect(() => {
    const handler = event => {
      let pending = 0
      for (const [, value] of Object.entries(uploads)) {
        if (value.status === UPLOADING || value.status === QUEUED) pending++
      }
      if (pending > 0) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [uploads])

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) requestHandleNewFiles(acceptedFiles)
  }, [requestHandleNewFiles])

  const uploadValues = Object.values(uploads)
  const finishedCount = uploadValues.filter(u => u.status === COMPLETE || u.status === FAILED || u.status === CANCELLED).length

  if (uploadsAccessChecked && !showUploadsTab) {
    return (
      <Alert variant="info">
        Uploads are not enabled for your account. Contact your Aberdeen account manager if you'd like to use them.
      </Alert>
    )
  }

  const hasVersions = fetched && eligible && versions.length > 0
  const versionSelected = hasVersions && versions.some(v => v.id === selectedVersionId)
  const canUpload = versionSelected && account && account.rootpath

  const dropzoneDisabledText = !fetched
    ? 'Loading…'
    : !hasVersions
      ? 'Uploads are not available for this account yet'
      : 'Select a Version first'

  // Section card with a title row, count and refresh button
  const sectionHeader = (title, count) => (
    <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold leading-tight">{title}</h2>
        <p className="text-xs text-muted-foreground">{`${count} file${count === 1 ? '' : 's'}`}</p>
      </div>
      <Button variant="outline" size="icon-sm" onClick={requestHubUploads} disabled={loading} title="Refresh">
        {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
      </Button>
    </div>
  )

  const uploadCard = (
    <Card className="border-primary/40 bg-primary/5 p-3 shadow-sm ring-1 ring-primary/10">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-stretch">
        <div className="lg:col-span-4">
          <Label htmlFor="upload-version" className="mb-1 flex items-center gap-1.5 text-xs">
            <UploadCloud className="h-3.5 w-3.5 text-primary" />
            <span className="font-semibold text-primary">Upload Files</span>
            {account ? <span className="font-normal text-muted-foreground">· {account.name}</span> : null}
            <span className="font-normal text-muted-foreground">· Version</span>
            <span className="text-destructive" aria-hidden="true">*</span>
          </Label>
          {hasVersions ? (
            <Select value={selectedVersionId || ''} onValueChange={requestSelectVersion}>
              <SelectTrigger id="upload-version" aria-required="true" className={cn('h-[3.25rem] bg-card', !versionSelected && 'border-primary/60')}>
                <SelectValue placeholder="Select the Version these files belong to" />
              </SelectTrigger>
              <SelectContent>
                {versions.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}{v.programName ? ` · ${v.programName}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex h-[3.25rem] items-center rounded-md border border-dashed px-3 text-xs text-muted-foreground">
              {fetched && eligible ? 'No Versions are set up for uploads on this account yet. Contact your Aberdeen account manager.' : 'Loading…'}
            </div>
          )}
        </div>
        <div className="lg:col-span-8 lg:pt-5">
          <UploadDropzone disabled={!canUpload} disabledText={dropzoneDisabledText} onDrop={onDrop} />
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Every file is attached to the selected Version. Keep this page open until all uploads finish; files take a few minutes to appear under Recent Uploads.
      </p>

      {rejected.length > 0 && (
        <div className="mt-3 space-y-2">
          {rejected.map(r => (
            <Alert key={r.id} variant="error" className="relative pr-9">
              <div>
                <p className="break-all font-medium">{r.name}</p>
                <p className="mt-0.5 text-sm opacity-90">{rejectionText(r)}</p>
              </div>
              <button
                type="button"
                onClick={() => requestRemoveRejected(r.id)}
                aria-label="Dismiss"
                className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </button>
            </Alert>
          ))}
        </div>
      )}

      {uploadValues.length > 0 && (
        <>
          <Separator className="my-3" />
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Uploads</span>
            {finishedCount > 0 && (
              <Button variant="ghost" size="xs" onClick={requestClearFinishedUploads}>
                <Trash2 className="h-3.5 w-3.5" /> Clear finished
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {uploadValues.map(value => (
              <div key={value.key}>
                <LongTermUploadProgress
                  s3key={value.key}
                  percentage={value.percentage}
                  filename={value.name}
                  totalBytes={value.totalBytesFriendly}
                  uploadedBytes={value.uploadedBytesFriendly}
                  timeLeftForUpload={value.timeLeftForUploadFriendly}
                  totalUploadTime={value.uploadTime}
                  status={value.status}
                  abortCallback={value.abortUpload}
                  errorMessage={value.errorMessage}
                />
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {value.versionName && <Badge variant="outline">{value.versionName}</Badge>}
                  {value.originalName && (
                    <span className="text-xs text-muted-foreground" title={value.originalName}>
                      Renamed from {value.originalName} (letters, numbers, _ and - only)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  )

  const recentCard = (
    <Card className="overflow-hidden">
      {sectionHeader('Recent Uploads', recentUploads.length)}
      <FilesTable
        files={recentUploads}
        page={recentPage}
        pageSize={recentPageSize}
        onPage={p => requestPage(RECENT, p)}
        onPageSize={n => requestPageSize(RECENT, n)}
        loading={loading}
        emptyText="No uploads yet. Files appear here a few minutes after they have been received."
        dateLabel="Uploaded"
        requestFileLink={requestFileLink}
      />
    </Card>
  )

  const versionOptions = longTermVersionOptions(longTermFiles)
  const filteredLongTermFiles = filterLongTermFiles(longTermFiles, longTermVersionFilter)

  const longTermCard = longTermEnabled && (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold leading-tight">Long Term Storage</h2>
          <p className="text-xs text-muted-foreground">
            {longTermVersionFilter === VERSION_FILTER_ALL
              ? `${longTermFiles.length} file${longTermFiles.length === 1 ? '' : 's'}`
              : `${filteredLongTermFiles.length} of ${longTermFiles.length} file${longTermFiles.length === 1 ? '' : 's'}`}
          </p>
        </div>
        {versionOptions.length > 0 && (
          <Select
            value={longTermVersionFilter || ALL_VERSIONS_ITEM}
            onValueChange={v => requestLongTermVersionFilter(v === ALL_VERSIONS_ITEM ? VERSION_FILTER_ALL : v)}
          >
            <SelectTrigger className="h-8 w-[220px] text-xs" aria-label="Filter by Version">
              <SelectValue placeholder="All Versions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VERSIONS_ITEM}>All Versions</SelectItem>
              {versionOptions.map(o => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="icon-sm" onClick={requestHubUploads} disabled={loading} title="Refresh">
          {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <FilesTable
        files={filteredLongTermFiles}
        page={longTermPage}
        pageSize={longTermPageSize}
        onPage={p => requestPage(LONG_TERM, p)}
        onPageSize={n => requestPageSize(LONG_TERM, n)}
        loading={loading}
        emptyText={longTermVersionFilter === VERSION_FILTER_ALL ? 'No files in long term storage yet.' : 'No long term files for this Version.'}
        dateLabel="Stored"
        requestFileLink={requestFileLink}
        onDelete={requestOpenFileDelete}
      />
    </Card>
  )

  return (
    <div className="space-y-3">
      {hasError && <Alert variant="error">{error}</Alert>}

      {/* Full width, stacked: slim uploader, recent uploads, long term storage */}
      {uploadCard}
      {recentCard}
      {longTermCard}

      {/* Delete confirmation */}
      <Dialog open={fileDelete.show} onOpenChange={o => { if (!o && !fileDelete.loading) requestCloseFileDelete() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete from Long Term Storage</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Permanently delete <span className="break-all font-medium">{fileDelete.file ? fileDelete.file.name : ''}</span> and
              its proxy from long term storage? This cannot be undone.
            </p>
            {fileDelete.hasError && <Alert variant="error">{fileDelete.error}</Alert>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={requestCloseFileDelete} disabled={fileDelete.loading}>Cancel</Button>
            <Button variant="destructive" onClick={requestConfirmFileDelete} disabled={fileDelete.loading}>
              {fileDelete.loading ? <><Spinner size={14} /> Deleting…</> : <><Trash2 className="h-3.5 w-3.5" /> Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download link / proxy player dialog */}
      <Dialog open={fileLink.show} onOpenChange={o => { if (!o) requestCloseFileLink() }}>
        <DialogContent
          className={cn(fileLink.mode === LINK_MODE_PLAY && 'max-w-4xl')}
          onOpenAutoFocus={e => { if (fileLink.mode === LINK_MODE_PLAY) e.preventDefault() }}
        >
          <DialogHeader>
            <DialogTitle>
              {fileLink.mode === LINK_MODE_PLAY ? 'Proxy Player' : fileLink.linkType === 'proxy' ? 'Download Proxy' : 'Download Source'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="break-all font-medium">{fileLink.fileName}</p>
            {fileLink.loading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Spinner size={18} /> {fileLink.mode === LINK_MODE_PLAY ? 'Loading the proxy…' : 'Preparing your link…'}
              </div>
            )}
            {fileLink.hasError && <Alert variant="error">{fileLink.error}</Alert>}
            {fileLink.link && fileLink.mode === LINK_MODE_PLAY && (
              <FocusedProxyPlayer
                key={fileLink.link}
                proxyLink={fileLink.link}
                frameRate={parseFloat(fileLink.file && fileLink.file.frameRate) || 29.97}
                aspectRatio={(fileLink.file && fileLink.file.videoAspectRatio) || '16:9'}
              />
            )}
            {fileLink.link && fileLink.mode !== LINK_MODE_PLAY && (
              <>
                <p className="text-muted-foreground">
                  {fileLink.linkType === 'proxy'
                    ? 'This is a downloadable link to the low-resolution proxy, not the full source file. The link expires in 7 days.'
                    : 'This is a downloadable link to the source file. The link expires in 7 days.'}
                </p>
                <a
                  href={fileLink.link}
                  target="_blank"
                  rel="noreferrer"
                  className="block break-all font-medium text-primary hover:underline"
                >
                  {fileLink.link}
                </a>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={requestCloseFileLink}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state, route) => {
  const {
    fetched,
    loading,
    hasError,
    error,
    eligible,
    account,
    versions,
    selectedVersionId,
    recentUploads,
    recentPage,
    recentPageSize,
    longTermEnabled,
    longTermFiles,
    longTermPage,
    longTermPageSize,
    longTermVersionFilter,
    uploads,
    rejected,
    fileLink,
    fileDelete
  } = state.stores.uploads

  const {
    uploadsAccessChecked,
    showUploadsTab
  } = state.stores.auth

  return {
    ...route.match.params,
    fetched,
    loading,
    hasError,
    error,
    eligible,
    uploadsAccessChecked,
    showUploadsTab,
    account,
    versions,
    selectedVersionId,
    recentUploads,
    recentPage,
    recentPageSize,
    longTermEnabled,
    longTermFiles,
    longTermPage,
    longTermPageSize,
    longTermVersionFilter,
    uploads,
    rejected,
    fileLink,
    fileDelete
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestHubUploads,
    requestLongTermVersionFilter,
    requestOpenFileDelete,
    requestCloseFileDelete,
    requestConfirmFileDelete,
    requestSelectVersion,
    requestHandleNewFiles,
    requestRemoveRejected,
    requestClearFinishedUploads,
    requestFileLink,
    requestCloseFileLink,
    requestPage,
    requestPageSize
  }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Uploads))
